import React, { useState, useRef } from 'react';
import {
  Box,
  InputLabel,
  TextField,
  Paper,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export const MultiSelectDropdown = ({
  label,
  placeholder = "Select options",
  value = [], // Array of selected values
  onChange,
  options = [], // Array of {label, value} objects
  name,
  errors,
  validation,
  isDisabled = false,
  width = '100%',
  mode = 'edit', // 'edit' or 'view'
  showChips = true, // Show selected items as chips
  maxDisplayItems = 3, // Maximum items to show in dropdown display
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle checkbox change
  const handleCheckboxChange = (optionValue) => (event) => {
    const { checked } = event.target;
    let updatedValues = [...value];

    if (checked) {
      // Add value if not already present
      if (!updatedValues.includes(optionValue)) {
        updatedValues.push(optionValue);
      }
    } else {
      // Remove value
      updatedValues = updatedValues.filter(val => val !== optionValue);
    }

    // Call onChange with updated array
    if (onChange) {
      onChange(name, updatedValues);
    }
  };

  // Handle click away
  const handleClickAway = () => {
    setIsOpen(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (mode !== 'view' && !isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  // Get display value for the text field
  const getDisplayValue = () => {
    if (!value || value.length === 0) {
      return '';
    }

    // Get labels for selected values
    const selectedLabels = options
      .filter(option => value.includes(option.value))
      .map(option => option.label);

    if (selectedLabels.length === 0) {
      return '';
    }

    if (selectedLabels.length <= maxDisplayItems) {
      return selectedLabels.join(', ');
    }

    return `${selectedLabels.slice(0, maxDisplayItems).join(', ')} (+${selectedLabels.length - maxDisplayItems} more)`;
  };

  // Remove chip
  const removeChip = (valueToRemove) => {
    const updatedValues = value.filter(val => val !== valueToRemove);
    if (onChange) {
      onChange(name, updatedValues);
    }
  };

  // Get label for value
  const getLabelForValue = (val) => {
    const option = options.find(opt => opt.value === val);
    return option ? option.label : val;
  };

  return (
    <Box sx={{ position: 'relative', width }}>
      {/* Label */}
      <InputLabel
        htmlFor={name}
        component="div"
        sx={{
          fontWeight: 600,
          fontSize: 16,
          color: '#000',
          marginBottom: 1,
          ...(errors?.status && { color: 'red' })
        }}
      >
        {label}
        {validation?.required && (
          <Typography component="span" sx={{ color: 'red', marginLeft: '4px' }}>
            *
          </Typography>
        )}
      </InputLabel>

      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          {/* Text field display */}
          <TextField
            fullWidth
            value={getDisplayValue()}
            placeholder={placeholder}
            disabled={mode === 'view' || isDisabled}
            onClick={toggleDropdown}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={toggleDropdown}
                  disabled={mode === 'view' || isDisabled}
                  sx={{ color: 'rgba(0, 0, 0, 0.54)' }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </IconButton>
              ),
            }}
            sx={{
              cursor: (mode === 'view' || isDisabled) ? 'default' : 'pointer',
              '& .MuiInputBase-input': {
                cursor: (mode === 'view' || isDisabled) ? 'default' : 'pointer',
              },
            }}
          />

          {/* Chips display (optional) */}
          {showChips && value && value.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.map((val) => (
                <Chip
                  key={val}
                  label={getLabelForValue(val)}
                  onDelete={mode !== 'view' && !isDisabled ? () => removeChip(val) : undefined}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    borderColor: '#2196f3',
                    '& .MuiChip-deleteIcon': {
                      color: '#2196f3',
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Dropdown menu */}
          {isOpen && (
            <Paper
              ref={dropdownRef}
              elevation={4}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: 300,
                overflowY: 'auto',
                zIndex: 1300,
                marginTop: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              {options.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No options available
                  </Typography>
                </Box>
              ) : (
                options.map((option) => (
                  <Box
                    key={option.value}
                    sx={{
                      padding: '8px 12px',
                      borderBottom: '1px solid #f5f5f5',
                      '&:hover': {
                        backgroundColor: '#f9f9f9'
                      },
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={value.includes(option.value)}
                          onChange={handleCheckboxChange(option.value)}
                          sx={{ padding: '4px' }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '14px' }}>
                          {option.label}
                        </Typography>
                      }
                      sx={{ 
                        margin: 0,
                        width: '100%',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px',
                          flex: 1
                        }
                      }}
                    />
                  </Box>
                ))
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {/* Error message */}
      {errors?.status && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {errors.message}
        </Typography>
      )}
    </Box>
  );
};